// src/components/SessionExpiredModal/SessionExpiredModal.jsx
import { useState, useEffect, useCallback } from 'react';
import styles from './SessionExpiredModal.module.css';
import { useTranslation } from 'react-i18next';

/**
 * Modal que se muestra cuando la sesión del usuario ha expirado.
 * Escucha el evento personalizado 'session-expired' disparado por el interceptor de axiosInstance.
 * Al cerrar el modal, redirige al login.
 *
 * La limpieza de userData se hace automáticamente en UserContext
 * (también escucha el evento session-expired).
 */
const SessionExpiredModal = () => {
  const [isVisible, setIsVisible] = useState(false);
  const { t } = useTranslation();

  const handleSessionExpired = useCallback(() => {
    // No mostrar si ya fue descartado en esta sesión de pestaña
    if (sessionStorage.getItem('session-expired-dismissed') === 'true') return;
    setIsVisible(true);
  }, []);

  useEffect(() => {
    // Limpiar flag al montar (p.ej. después de un login exitoso que recargó)
    sessionStorage.removeItem('session-expired-dismissed');

    window.addEventListener('session-expired', handleSessionExpired);
    return () => {
      window.removeEventListener('session-expired', handleSessionExpired);
    };
  }, [handleSessionExpired]);

  const handleClose = () => {
    setIsVisible(false);
    // Marcar como descartado para evitar que reaparezca tras el reload
    sessionStorage.setItem('session-expired-dismissed', 'true');
    // Redirigir al login (hard reload para limpiar todo el estado)
    window.location.href = '/login';
  };

  if (!isVisible) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.iconContainer}>
          <svg
            className={styles.icon}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 8V12M12 16H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <h2 className={styles.title}>{t('sessionExpired.title')}</h2>
        <p className={styles.message}>
          {t('sessionExpired.message')}
        </p>
        <button className={styles.button} onClick={handleClose}>
          {t('sessionExpired.loginButton')}
        </button>
      </div>
    </div>
  );
};

export default SessionExpiredModal;
